import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, FileText, ArrowRight, MailWarning, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SERVICE_MAP, STATUS_META } from "@/data/services";
import { DeadlinePill } from "@/components/DeadlineWatch";

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.submitted;
  return <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${m.cls}`} data-testid={`status-${status}`}>{m.label}</span>;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [apps, setApps] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/applications"); setApps(data); }
    catch (e) { toast.error(apiErr(e)); setApps([]); }
  };
  useEffect(() => { load(); }, []);

  const resend = async () => {
    try { await api.post("/auth/resend-verification", { email: user.email }); toast.success("Verification email sent."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-brand-red font-bold">Dashboard</p>
          <h1 className="font-serif text-4xl text-brand-navy tracking-tight mt-1">Hello, {user?.name?.split(" ")[0] || "there"}</h1>
          <p className="text-slate-500 mt-1">Manage your Vietnam immigration applications.</p>
        </div>
        <Link to="/apply" data-testid="new-application-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-reddark transition-colors shadow-redglow">
          <Plus className="w-4 h-4" /> New application
        </Link>
      </div>

      {!user?.is_verified && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="dashboard-verify-banner">
          <MailWarning className="w-5 h-5 text-brand-amber shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Your email isn't verified yet.</strong> Verify it to submit applications.
            <button onClick={resend} className="ml-2 font-semibold underline" data-testid="dashboard-resend-btn">Resend verification email</button>
          </div>
        </div>
      )}

      <div className="mt-10">
        {apps === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-brand-red animate-spin" /></div>
        ) : apps.length === 0 ? (
          <div className="text-center py-20 rounded-3xl border border-dashed border-slate-300 bg-slate-50" data-testid="empty-applications">
            <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200 flex items-center justify-center mx-auto mb-4 text-slate-400"><Inbox className="w-7 h-7" /></div>
            <h3 className="font-semibold text-brand-navy text-lg">No applications yet</h3>
            <p className="text-slate-500 mt-1">Start your first Vietnam immigration application.</p>
            <Link to="/apply" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-navy text-white font-semibold hover:bg-slate-800 transition-colors">Start now <ArrowRight className="w-4 h-4" /></Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {apps.map((a, i) => {
              const svc = SERVICE_MAP[a.service_type];
              return (
                <motion.div key={a.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link to={`/applications/${a.id}`} data-testid={`application-row-${a.id}`}
                    className="group flex flex-wrap items-center gap-4 justify-between rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-soft hover:border-slate-300 transition-all">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-xl bg-brand-red/10 flex items-center justify-center text-brand-red"><FileText className="w-5 h-5" /></div>
                      <div>
                        <h3 className="font-semibold text-brand-navy">{svc?.title || a.service_type}</h3>
                        <p className="text-xs text-slate-500 font-mono">{a.reference_no}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <StatusPill status={a.status} />
                      <DeadlinePill app={a} />
                      <span className="text-xs text-slate-400 hidden sm:block">{(a.created_at || "").slice(0, 10)}</span>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-brand-red group-hover:translate-x-1 transition-all" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
